import Collection from '@model-core/core/Collection';
import ExportModel, { exportUrn, type ExportFilters } from '@/models/entities/ExportModel';
import FileModel from '@/models/entities/FileModel';
import PeriodModel from '@/models/entities/PeriodModel';
import TeamModel from '@/models/entities/TeamModel';
import { companyUrn } from '@/models/entities/CompanyModel';
import type { PageRequest } from '@/models/listPage';
import { resolveReference, type ReferenceOption } from '@/stores/useReferenceOptions';

/** Intervalle de suivi d'un export en attente ou en cours (Mercure plus tard, cf. suivi technique) */
export const EXPORT_POLL_MS = 2000;

/**
 * Lance un export des feuilles de la société (POST /exports) : l'API fige la requête de la liste (mêmes filtres, mêmes
 * droits), puis génère le fichier en asynchrone. Réservé au Dirigeant.
 */
export async function createExport(
  companyId: number,
  typeReference: string,
  filters: ExportFilters,
): Promise<ExportModel> {
  const type = await resolveReference('exportTypes', typeReference);
  const { model } = await new ExportModel().save(
    { company: companyUrn(companyId), exportType: type.urn, filters },
    { silentError: true },
  );

  return model;
}

/** Les derniers exports de la société, du plus récent au plus ancien */
export function latestExportsRequest(companyId: number, itemsPerPage: number): PageRequest {
  return {
    filters: { company: companyId },
    order: { createdAt: 'desc', id: 'desc' },
    page: 1,
    itemsPerPage,
    partial: true,
  };
}

/**
 * Télécharge le fichier d'un export terminé : son fichier (par l'URN de l'export), puis son adresse temporaire (S3
 * signée, 5 minutes), ouverte par un lien : le navigateur enregistre le CSV ou le ZIP
 */
export async function downloadExport(exportId: id | number): Promise<void> {
  const { collection } = await new Collection(FileModel)
    .setFilter('typeSource.source.entityUrn', exportUrn(exportId))
    .list();
  const [file] = collection.models;
  if (!file) throw "Le fichier de l'export est introuvable.";

  const link = document.createElement('a');
  // En pièce jointe : le navigateur enregistre le fichier au lieu d'afficher le CSV (S3 l'enverrait en ligne)
  link.href = await file.downloadUrl(true);
  link.download = file.name;
  link.rel = 'noopener';
  link.click();
}

/**
 * Filtres de la liste à enregistrer sur l'export : ceux de sa requête GET /time_sheets, sans les vides ni la
 * pagination, et son tri (order)
 */
export function exportFiltersOf(request: PageRequest): ExportFilters {
  const filters: ExportFilters = {};
  for (const [key, value] of Object.entries(request.filters ?? {})) {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) continue;
    filters[key] = value;
  }
  if (request.order && Object.keys(request.order).length) filters.order = { ...request.order };

  return filters;
}

/** Noms des périodes et des équipes citées par les filtres des exports, par id */
export interface FilterNames {
  periods: Map<number, string>;
  teams: Map<number, string>;
}

/** Ids cités par un filtre (une valeur ou une liste) */
const idsOf = (value: unknown): number[] =>
  (Array.isArray(value) ? value : value === undefined || value === null ? [] : [value]).map(Number).filter(Boolean);

export async function loadFilterNames(exports: ExportModel[]): Promise<FilterNames> {
  const periodIds = [...new Set(exports.flatMap((model) => idsOf(model.filters.period)))];
  const teamIds = [...new Set(exports.flatMap((model) => idsOf(model.filters['teamMember.team'])))];
  const [periods, teams] = await Promise.all([
    periodIds.length
      ? new Collection(PeriodModel).setFilter('id' as never, periodIds.map(String) as never).list()
      : null,
    teamIds.length ? new Collection(TeamModel).setFilter('id' as never, teamIds.map(String) as never).list() : null,
  ]);

  return {
    periods: new Map((periods?.collection.models ?? []).map((period) => [Number(period.id), period.monthLabel])),
    teams: new Map((teams?.collection.models ?? []).map((team) => [Number(team.id), team.name])),
  };
}

/**
 * Résumé lisible des filtres d'un export : « Septembre 2026 · DEV · Absence · « doe » » ; « Toutes les feuilles » sans
 * filtre. Les statuts (feuilles soumises) et le tri ne sont pas repris.
 */
export function describeFilters(
  filters: ExportFilters,
  names: FilterNames | null,
  timeSheetTypes: ReferenceOption[],
): string {
  const parts = [
    ...idsOf(filters.period).map((id) => names?.periods.get(id) ?? `Période ${id}`),
    ...idsOf(filters['teamMember.team']).map((id) => names?.teams.get(id) ?? `Équipe ${id}`),
    ...idsOf(filters.timeSheetType).map((id) => timeSheetTypes.find((type) => type.id === id)?.label ?? `Type ${id}`),
    ...(typeof filters.search === 'string' && filters.search ? [`« ${filters.search} »`] : []),
  ];

  return parts.length ? parts.join(' · ') : 'Toutes les feuilles';
}
